const pad = (value) => String(value).padStart(2, '0');

/**
 * "2026-01-01" for a local date. Never through `toISOString`: that converts to
 * UTC first, and a local midnight east of UTC would land on the day before.
 */
export const toIsoDay = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Local midnight of 1 January of the current year. */
export const startOfCurrentYear = () =>
  new Date(new Date().getFullYear(), 0, 1);

/** Local midnight of today. */
export const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const FILE_NAME_FALLBACK = 'entreprise';
const FILE_NAME_SLUG_MAX_LENGTH = 60;

/**
 * Same algorithm as the api's toFileNameSlug, so both name the file alike:
 * ASCII `[a-z0-9-]`, accents dropped, œ and æ spelt out, cut at 60 characters.
 */
export const slugifyCompanyName = (name) => {
  const slug = (typeof name === 'string' ? name : '')
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, FILE_NAME_SLUG_MAX_LENGTH)
    .replace(/^-+|-+$/g, '');
  return slug || FILE_NAME_FALLBACK;
};

/**
 * Named here, on the api's pattern: CORS does not expose Content-Disposition,
 * so the name the api gives cannot be read.
 */
export const accountStatementFileName = (companyName, from, to) =>
  `releve-de-compte-${slugifyCompanyName(companyName)}-${from}-${to}.pdf`;

const ERROR_TEXTS = {
  PENNYLANE_CUSTOMER_NOT_FOUND: {
    key: 'customerNotFound',
    fallback: 'Cette entreprise n’est pas reliée à un client Pennylane.',
  },
  PENNYLANE_CUSTOMER_AMBIGUOUS: {
    key: 'customerNotFound',
    fallback: 'Cette entreprise n’est pas reliée à un client Pennylane.',
  },
  PENNYLANE_UNAVAILABLE: {
    key: 'pennylaneUnavailable',
    fallback: 'Pennylane ne répond pas, réessayez dans quelques minutes.',
  },
  PENNYLANE_READS_DISABLED: {
    key: 'readsDisabled',
    fallback: 'La lecture de Pennylane est désactivée sur cet environnement.',
  },
  ACCOUNT_STATEMENT_TOO_LARGE: {
    key: 'tooLarge',
    fallback: 'Ce compte contient trop d’écritures pour générer le relevé (plus de 5 000 lignes).',
  },
  INVALID_PERIOD: {
    key: 'invalidPeriod',
    fallback: 'La période est invalide.',
  },
  ACCOUNT_STATEMENT_FORBIDDEN: {
    key: 'forbidden',
    fallback: 'Vous n’avez pas accès au relevé de compte.',
  },
};
const DEFAULT_ERROR_TEXT = {
  key: 'failed',
  fallback: 'Le relevé n’a pas pu être généré.',
};

const MAIL_FAILED_TEXT = {
  key: 'mailFailed',
  fallback: 'Le mail n’a pas pu partir, réessayez.',
};
// The mail reads the statement first: its refusals keep their text.
const MAIL_ERROR_TEXTS = {
  ...ERROR_TEXTS,
  RECIPIENTS_INVALID: {
    key: 'recipientsInvalid',
    fallback:
      'Un destinataire coché n’est plus un contact de facturation : fermez et rouvrez la fenêtre.',
  },
  BILLING_MAIL_DISABLED: {
    key: 'mailDisabled',
    fallback: 'L’envoi de mails est coupé sur cet environnement.',
  },
  BILLING_MAIL_SEND_FAILED: MAIL_FAILED_TEXT,
  ATTACHMENTS_TOO_LARGE: MAIL_FAILED_TEXT,
};
const MAIL_NETWORK_TEXT = {
  key: 'mailNetwork',
  fallback: 'Le serveur n’a pas répondu : le mail est peut-être déjà parti.',
};

const textOf = (language, { key, fallback }) =>
  language.getValue(`accountStatement.errors.${key}`, fallback);

/** The text shown for an api `errorCode`; any other code gets the generic one. */
export const accountStatementErrorText = (language, errorCode) =>
  textOf(language, ERROR_TEXTS[errorCode] ?? DEFAULT_ERROR_TEXT);

/**
 * The text of a refused mail. Without an answer the mail may have left: the
 * api keeps no request id for a statement, so the text says so.
 */
export const accountStatementMailErrorText = (language, error) => {
  if (!error?.response) return textOf(language, MAIL_NETWORK_TEXT);
  const errorCode = error.response.data?.errorCode;
  return textOf(language, MAIL_ERROR_TEXTS[errorCode] ?? MAIL_FAILED_TEXT);
};
